import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, Calendar, Award, PlayCircle, FileText, CheckCircle, Lock, Clock, Presentation, Download, Timer, ClipboardCheck, FileEdit, Upload, File as FileIcon, X, ShoppingBag, Phone } from 'lucide-react';
import { fetchPrograms, fetchCourses, fetchQuiz, fetchTrainers, fetchUserProgress, fetchRecevabilite, fetchLivret2Corrections, trackTime, uploadRecevabiliteFile, getFileUrl, fetchBookings, getUserPayments, getUserPurchasedPrograms, getAccompaniment, createCheckout, rescheduleBooking, fetchSlots, deletePendingPayment } from '../services/api';
import { toast } from 'sonner';
import { getActivityProgress, getAllProgress, markQuizCompleted, loadProgressFromBackend } from '../utils/progressTracker';
import StressManagementTab from '../components/dashboard/StressManagementTab';
import CourseDetailTab from '../components/dashboard/CourseDetailTab';
import OverviewTab from '../components/dashboard/OverviewTab';
import CoursesTab from '../components/dashboard/CoursesTab';
import BookingsTab from '../components/dashboard/BookingsTab';
import OralPrepTab from '../components/dashboard/OralPrepTab';
import PurchasesTab from '../components/dashboard/PurchasesTab';
import VeilleProTab from '../components/dashboard/VeilleProTab';
import MonParcoursTab from '../components/dashboard/MonParcoursTab';
import RessourcesGratuitesTab from '../components/dashboard/RessourcesGratuitesTab';

const DashboardPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'overview');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [userProgressData, setUserProgressData] = useState({});
  const [VAE_PROGRAMS, setPrograms] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [MOCK_TRAINERS, setTrainers] = useState([]);
  const [quizDataCache, setQuizDataCache] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [userProgressList, setUserProgressList] = useState([]);
  const [recevabiliteList, setRecevabiliteList] = useState([]);
  const [livret2Corrections, setLivret2Corrections] = useState([]);
  const [courseTimeStart, setCourseTimeStart] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [userBookings, setUserBookings] = useState([]);
  const [userPurchases, setUserPurchases] = useState([]);
  const [purchasedProgramIds, setPurchasedProgramIds] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [accompanimentData, setAccompanimentData] = useState(null);
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleSlot, setRescheduleSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);

  // Get real user from localStorage (set during login)
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const user = {
    id: storedUser.id || 'user-1',
    email: storedUser.email || 'candidat@example.com',
    name: storedUser.name || 'Candidat',
    role: storedUser.role || 'candidate',
    avatar: storedUser.role === 'admin' 
      ? 'https://customer-assets.emergentagent.com/job_a78a7f7c-4ffa-42d5-ad51-b7bdfa49124a/artifacts/m0c1aozf_avatar.png'
      : null,
    enrolledPrograms: storedUser.role === 'admin' 
      ? ['cap-aepe', 'deap', 'deeje', 'caferuis', 'deas', 'bac-assp', 'tp'] 
      : purchasedProgramIds.length > 0 ? purchasedProgramIds : [],
    progress: {}
  };

  // Fetch all data from API
  useEffect(() => {
    async function loadData() {
      try {
        const [programs, courses, trainers] = await Promise.all([
          fetchPrograms(),
          fetchCourses(),
          fetchTrainers()
        ]);
        setPrograms(programs);
        setAllCourses(courses);
        setTrainers(trainers);
        // Load user-specific data
        fetchUserProgress(user.id).then(setUserProgressList).catch(() => {});
        fetchRecevabilite(user.id).then(setRecevabiliteList).catch(() => {});
        fetchLivret2Corrections(user.id).then(setLivret2Corrections).catch(() => {});
        fetchBookings(user.id).then(setUserBookings).catch(() => {});
        getUserPayments(user.email).then(setUserPurchases).catch(() => {});
        getUserPurchasedPrograms(user.email).then(data => {
          const ids = data.map(p => p.programId);
          setPurchasedProgramIds(ids);
          if (ids.length > 0 && !selectedProgramId) {
            setSelectedProgramId(ids[0]);
          }
          // Extract subscription dates from the most relevant purchase
          const mainPurchase = data.find(p => p.formula && p.formula !== 'kit') || data.find(p => p.kitId) || data[0];
          if (mainPurchase && mainPurchase.created_at) {
            const startDate = new Date(mainPurchase.created_at);
            const endDate = mainPurchase.expires_at ? new Date(mainPurchase.expires_at) : new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000);
            setSubscriptionInfo({ startDate, endDate, expired: new Date() > endDate });
          }
        }).catch(() => {});
        // Admin sees all
        if (storedUser.role === 'admin' && programs.length > 0 && !selectedProgramId) {
          setSelectedProgramId(programs[0].id);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setDataLoading(false);
      }
    }
    loadData();
  }, []);

  // Load progress data from backend on mount, then from cache when navigating
  useEffect(() => {
    if (user.id && user.id !== 'user-1') {
      loadProgressFromBackend().then(data => {
        if (data) setUserProgressData(data);
      });
    } else {
      setUserProgressData(getAllProgress());
    }
  }, []);

  useEffect(() => {
    setUserProgressData(getAllProgress());
  }, [activeTab, selectedCourse]);

  // Load accompaniment tracking when program changes
  useEffect(() => {
    if (selectedProgramId && user.id && user.role !== 'admin') {
      getAccompaniment(user.id, selectedProgramId).then(setAccompanimentData).catch(() => setAccompanimentData(null));
    }
  }, [selectedProgramId]);


  // Load quiz data on demand when a quiz course is selected
  useEffect(() => {
    if (selectedCourse?.type === 'quiz' && !quizDataCache[selectedCourse.id]) {
      fetchQuiz(selectedCourse.id).then(data => {
        setQuizDataCache(prev => ({ ...prev, [selectedCourse.id]: data }));
      }).catch(console.error);
    }
  }, [selectedCourse]);

  // Periodic time tracking (every 30s while course is open)
  useEffect(() => {
    if (!selectedCourse || !courseTimeStart) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - courseTimeStart) / 1000);
      if (elapsed > 5) {
        trackTime(selectedCourse.id, elapsed, user.id).catch(() => {});
        setCourseTimeStart(Date.now());
      }
    }, 30000);
    return () => {
      clearInterval(interval);
      // Save remaining time on unmount
      if (courseTimeStart) {
        const elapsed = Math.floor((Date.now() - courseTimeStart) / 1000);
        if (elapsed > 2) {
          trackTime(selectedCourse.id, elapsed, user.id).catch(() => {});
        }
      }
    };
  }, [selectedCourse, courseTimeStart]);

  const enrolledProgram = VAE_PROGRAMS.find(p => p.id === selectedProgramId);
  const allProgramCourses = allCourses.filter(c => c.programId === selectedProgramId);

  // === PROGRESSION GATING: Bloc order per program ===
  const BLOC_ORDER = {
    caferuis: ['bc1', 'bc2', 'bc3', 'bc4', 'bc5'],
    deap: ['bloc1', 'bloc2', 'bloc3', 'bloc4', 'bloc5'],
    'bac-assp': ['bloc1', 'bloc2', 'bloc3', 'bloc4', 'pse', 'outils'],
    tp: ['bloc1', 'bloc2', 'bloc3'],
    deeje: ['pedagogues', 'glossaire', 'lois', 'dc1', 'dc2', 'dc3', 'dc4'],
  };

  // Resolve effective bloc: prefer blocId if in BLOC_ORDER, else fallback to dcId
  const getEffectiveBloc = (course, order) => {
    if (course.blocId && order && order.includes(course.blocId)) return course.blocId;
    if (course.dcId && order && order.includes(course.dcId)) return course.dcId;
    return course.blocId || course.dcId;
  };

  const isBlocCompleted = (courses, blocId, progressData) => {
    const order = BLOC_ORDER[selectedProgramId];
    const blocCourses = courses.filter(c => {
      const cBloc = getEffectiveBloc(c, order);
      return cBloc === blocId && !c.isHeader && c.type !== 'bloc-header';
    });
    const presentations = blocCourses.filter(c => c.type === 'presentation');
    const quizzes = blocCourses.filter(c => c.type === 'quiz');

    // Presentation considered complete if >= 90% of slides viewed (handles tracking edge cases)
    const allPresCompleted = presentations.length > 0 && presentations.every(p => {
      const key = p.presentationType || p.id;
      const prog = progressData[key];
      if (!prog || !prog.totalSlides || prog.totalSlides <= 0) return false;
      const viewed = prog.viewedSlides ? prog.viewedSlides.length : 0;
      return viewed >= Math.ceil(prog.totalSlides * 0.9);
    });

    // If quiz is passed (>=70%), consider the bloc complete even if not all slides viewed
    const allQuizzesPassed = quizzes.length > 0 && quizzes.every(q => {
      const key = q.presentationType || q.id;
      const prog = progressData[key];
      return prog && prog.quizCompleted && prog.quizScore != null && prog.quizTotalQuestions &&
        (prog.quizScore / prog.quizTotalQuestions) >= 0.7;
    });

    if (allQuizzesPassed) return true;
    if (quizzes.length === 0) return allPresCompleted;

    return allPresCompleted && allQuizzesPassed;
  };

  const computeLockedCourses = (courses) => {
    // No progression locking — all courses accessible immediately
    return courses.map(c => ({ ...c, locked: false }));
  };

  // Formula entitlements per tier, per program
  const FORMULA_ENTITLEMENTS_BY_PROGRAM = {
    deap: {
      bronze: { seances: 0, corrections: 0, appels: 0, oralBlanc: 0, presentationPerso: 0, label: 'Bronze', includes: ['E-learning complet', 'Livret 2 annoté'] },
      silver: { seances: 5, corrections: 5, appels: 5, oralBlanc: 1, presentationPerso: 1, label: 'Argent', includes: ['E-learning complet', 'Livret 2 annoté', '5 séances de coaching', '5 corrections Livret 2', '1 oral blanc', '1 présentation personnalisée'] },
      gold: { seances: 999, corrections: 999, appels: 999, oralBlanc: 3, presentationPerso: 2, label: 'Or', duree: '1 an', includes: ['E-learning complet', 'Livret 2 annoté', 'Séances illimitées', 'Corrections illimitées', '3 oraux blancs', '2 présentations personnalisées', 'Accès 1 an'] }
    },
    deeje: {
      bronze: { seances: 0, corrections: 0, appels: 0, oralBlanc: 0, presentationPerso: 0, label: 'Bronze', includes: ['E-learning complet', 'Livret 2 annoté'] },
      silver: { seances: 6, corrections: 6, appels: 6, oralBlanc: 1, presentationPerso: 1, label: 'Argent', includes: ['E-learning complet', 'Livret 2 annoté', '6 séances de coaching', '6 corrections Livret 2', '1 oral blanc', '1 présentation personnalisée'] },
      gold: { seances: 999, corrections: 999, appels: 999, oralBlanc: 3, presentationPerso: 2, label: 'Or', duree: '1 an', includes: ['E-learning complet', 'Livret 2 annoté', 'Séances illimitées', 'Corrections illimitées', '3 oraux blancs', '2 présentations personnalisées', 'Accès 1 an'] }
    },
    'bac-assp': {
      bronze: { seances: 0, corrections: 0, appels: 0, oralBlanc: 0, presentationPerso: 0, label: 'Bronze', includes: ['Formation en ligne complète', 'Accès illimité à la plateforme e-learning', 'Méthodologie détaillée pour chaque bloc', 'Quiz et entraînements interactifs', 'Supports écrits et ressources téléchargeables'] },
      silver: { seances: 6, corrections: 5, appels: 0, oralBlanc: 1, presentationPerso: 0, label: 'Argent', includes: ['Tout Bronze inclus', '6 séances d\'accompagnement individuel', 'Aide à la rédaction du Livret 2', '5 corrections approfondies du Livret 2 (1 par bloc)', '1 oral blanc de préparation avec débrief complet', 'Support par email prioritaire'] },
      gold: { seances: 999, corrections: 999, appels: 999, oralBlanc: 3, presentationPerso: 2, label: 'Or', duree: '1 an', includes: ['Tout Argent inclus', 'Accompagnement illimité pendant 1 an', 'Corrections illimitées et approfondies', '3 oraux blancs de préparation', 'Préparation approfondie et simulation d\'oral', 'Support prioritaire'] }
    },
    tp: {
      bronze: { seances: 0, corrections: 0, appels: 0, oralBlanc: 0, presentationPerso: 0, rdvDiaporama: 0, entretienTechnique: 0, entretienDiaporama: 0, entretienFinal: 0, label: 'Bronze', includes: ['Plateforme e-learning illimitée 1 an (3 blocs)', 'Livret 2 détaillé fourni', 'Trame du diaporama prêt à remplir', 'Quiz et entraînements interactifs'] },
      silver: { seances: 0, corrections: 5, appels: 5, oralBlanc: 0, presentationPerso: 0, rdvDiaporama: 2, entretienTechnique: 1, entretienDiaporama: 1, entretienFinal: 1, label: 'Argent', includes: ['Tout Bronze inclus', '3 corrections du livret (1 par bloc)', '2 corrections personnalisation du diaporama', '5 appels explicatifs (3 livret + 2 diaporama)', '1 entretien technique blanc (1h35)', '1 entretien diaporama blanc (30 min)', '1 entretien final blanc (15 min)'] },
      gold: { seances: 0, corrections: 999, appels: 999, oralBlanc: 0, presentationPerso: 0, rdvDiaporama: 2, entretienTechnique: 2, entretienDiaporama: 2, entretienFinal: 2, label: 'Or', duree: '1 an', includes: ['Tout Argent inclus', 'Corrections illimitées', 'Échange SMS/mail illimité pendant 1 an', '2 entretiens techniques blancs (1h35)', '2 entretiens diaporama blancs (30 min)', '2 entretiens finaux blancs (15 min)'] }
    },
    caferuis: {
      bronze: { seances: 0, corrections: 0, appels: 0, oralBlanc: 0, presentationPerso: 0, label: 'Bronze', includes: ['Formation en ligne complète', 'Accès illimité à la plateforme e-learning', 'Méthodologie détaillée pour chaque bloc de compétences', 'Quiz et entraînements interactifs', 'Supports écrits et ressources téléchargeables'] },
      silver: { seances: 6, corrections: 5, appels: 0, oralBlanc: 1, presentationPerso: 0, label: 'Argent', includes: ['Tout Bronze inclus', '6 séances d\'accompagnement individuel', 'Aide à la rédaction du Livret 2', '5 corrections approfondies de votre Livret 2 (1 par bloc)', '1 oral blanc de préparation avec débrief complet', 'Assistance par email prioritaire'] },
      gold: { seances: 999, corrections: 999, appels: 999, oralBlanc: 3, presentationPerso: 0, label: 'Or', duree: '1 an', includes: ['Tout Argent inclus', 'Accompagnement illimité pendant 1 an', 'Corrections illimitées et approfondies', '3 oraux blancs de préparation', 'Préparation approfondie et simulation d\'oral', 'Échange par SMS et mail illimité', 'Soutien prioritaire'] }
    }
  };
  const programEntitlements = FORMULA_ENTITLEMENTS_BY_PROGRAM[selectedProgramId] || FORMULA_ENTITLEMENTS_BY_PROGRAM.deap;
  const currentPurchase = userPurchases.find(p => p.programId === selectedProgramId && p.status === 'completed' && (p.formula || p.metadata?.formula));
  const rawFormula = (currentPurchase?.formula || currentPurchase?.metadata?.formula || null)?.toLowerCase() || null;
  const isKit = rawFormula === 'kit' || (rawFormula && rawFormula.startsWith('kit '));
  // Try to resolve kitId from: root field > metadata > extract from formula title
  const resolvedKitId = isKit ? (
    currentPurchase?.kitId || currentPurchase?.metadata?.kit_id || 
    // Fallback: try to match kit from formula title against program kit definitions
    (() => {
      const kitTitle = (currentPurchase?.metadata?.kit_title || currentPurchase?.metadata?.formula || rawFormula || '').toLowerCase();
      const programKits = (VAE_PROGRAMS.find(p => p.id === selectedProgramId)?.kits || []);
      const matched = programKits.find(k => kitTitle.includes(k.title?.toLowerCase()) || k.title?.toLowerCase().includes(kitTitle.replace('kit ', '')));
      if (matched) return matched.id;
      // Last resort: extract number from formula like "kit 4 — pédagogues..."
      const numMatch = kitTitle.match(/kit\s*(\d+)/);
      if (numMatch) {
        const num = numMatch[1];
        const byNum = programKits.find(k => k.id?.includes(`-${num}`) || k.id?.includes(`${num}-`));
        if (byNum) return byNum.id;
      }
      return null;
    })()
  ) : null;
  const kitId = resolvedKitId || null;
  const kitBlocNum = kitId ? (kitId.match(/\d+/)?.[0] || kitId.replace('kit-bloc-', '')) : null;
  const kitDownloadPath = kitId 
    ? (selectedProgramId === 'deeje' ? `/downloads/deeje/${kitId}.pdf` : `/documents/kit-bloc-${kitBlocNum}.pdf`)
    : null;
  // Get kit metadata from program data
  const currentKitData = isKit && kitId && VAE_PROGRAMS.length > 0
    ? (VAE_PROGRAMS.find(p => p.id === selectedProgramId)?.kits || []).find(k => k.id === kitId)
    : null;
  const kitHasElearning = isKit ? (currentKitData ? currentKitData.hasElearning === true : !['kit-1-demarrer', 'kit-6-analyse', 'kit-3-dc2'].includes(kitId)) : true;
  const currentFormula = isKit ? 'kit' : rawFormula;
  const entitlements = programEntitlements[currentFormula] || programEntitlements.bronze;
  const confirmedBookings = userBookings.filter(b => b.status === 'confirmed' && b.programId === selectedProgramId && b.type !== 'decouverte' && !b.isDecouverte).length;
  const activeBookingsCount = userBookings.filter(b => b.status !== 'cancelled' && b.type !== 'decouverte' && !b.isDecouverte).length;
  const decouverteBooking = userBookings.find(b => b.type === 'decouverte' || b.isDecouverte);
  const hasStressAccess = currentFormula === 'gold' || userPurchases.some(p => p.kitId === 'gestion-stress' && p.status === 'completed');
  const hasPresentationPersoAccess = currentFormula === 'gold' || currentFormula === 'silver' || userPurchases.some(p => p.kitId === 'presentation-perso' && p.status === 'completed');

  // For kit purchases, only show courses from the purchased bloc/DC
  const rawUserCourses = (isKit
    ? (currentKitData
      ? (currentKitData.dcIds && currentKitData.dcIds.length > 0
        ? allProgramCourses.filter(c => currentKitData.dcIds.includes(c.dcId))
        : currentKitData.blocIds && currentKitData.blocIds.length > 0
          ? allProgramCourses.filter(c => currentKitData.blocIds.includes(c.blocId))
          : kitBlocNum ? allProgramCourses.filter(c => c.blocId === `bloc${kitBlocNum}`) : [])
      : kitBlocNum ? allProgramCourses.filter(c => c.blocId === `bloc${kitBlocNum}`) : [])
    : allProgramCourses
  ).filter(c => !c.id?.startsWith('course-intro-'));

  // Apply progression gating - dynamically compute locked status
  const userCourses = computeLockedCourses(rawUserCourses);

  const progress = {
    completedCourses: 0,
    totalCourses: userCourses.filter(c => !c.isHeader && !c.id?.includes('header')).length,
    completedSessions: confirmedBookings,
    totalSessions: entitlements.seances === 999 ? 'Illimité' : entitlements.seances,
    nextSession: userBookings.find(b => b.status === 'confirmed' && new Date(b.date) >= new Date())?.date || null
  };

  // Alias for quiz data to minimize code changes below
  const MOCK_QUIZ_DATA = quizDataCache;

  // Helper to get progress for a course
  const getCourseProgress = (course) => {
    if (course.presentationType) {
      return userProgressData[course.presentationType] || null;
    }
    return null;
  };

  const handleCourseClick = (course) => {
    // Save time for previous course if any
    if (selectedCourse && courseTimeStart) {
      const elapsed = Math.floor((Date.now() - courseTimeStart) / 1000);
      if (elapsed > 2) {
        trackTime(selectedCourse.id, elapsed, user.id).catch(() => {});
      }
    }
    setCourseTimeStart(Date.now());
    setSelectedCourse(course);
    setActiveTab('course-detail');
  };

  const handleCompleteCourse = () => {
    // Save time for current course
    if (selectedCourse && courseTimeStart) {
      const elapsed = Math.floor((Date.now() - courseTimeStart) / 1000);
      if (elapsed > 2) {
        trackTime(selectedCourse.id, elapsed, user.id).catch(() => {});
      }
      setCourseTimeStart(null);
    }
    toast.success('Cours terminé ! Vous pouvez passer au suivant.');
    setSelectedCourse(null);
    setActiveTab('courses');
    // Refresh progress
    fetchUserProgress(user.id).then(setUserProgressList).catch(() => {});
  };

  const handleQuizAnswer = (questionId, optionIndex) => {
    setQuizAnswers({
      ...quizAnswers,
      [questionId]: optionIndex
    });
  };

  const handleQuizSubmit = () => {
    if (!selectedCourse) return;
    
    const quizData = MOCK_QUIZ_DATA[selectedCourse.id];
    if (!quizData) return;

    let correct = 0;
    quizData.questions.forEach((q) => {
      if (quizAnswers[q.id] === q.correct) {
        correct++;
      }
    });

    const percentage = (correct / quizData.questions.length) * 100;
    setQuizSubmitted(true);
    
    // Save quiz progress - use presentationType or id as key
    markQuizCompleted(selectedCourse.presentationType || selectedCourse.id, correct, quizData.questions.length);
    setUserProgressData(getAllProgress());
    
    if (percentage >= 70) {
      toast.success(`Félicitations ! Vous avez réussi avec ${percentage.toFixed(0)}% — Le bloc suivant est débloqué !`);
    } else {
      toast.error(`Score : ${percentage.toFixed(0)}%. Il faut au moins 70% pour valider et débloquer le bloc suivant.`);
    }
  };

  const handleQuizRetry = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
  };

  if (dataLoading) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Chargement de votre espace...</p>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div className="container" style={{ padding: '2rem 1.2rem' }}>
        {/* Welcome Section */}
        <div className="card" style={{ 
          marginBottom: '2rem',
          background: 'var(--gradient-hero-warm)',
          padding: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid white'
                }}
              />
            ) : (
              <div style={{
                width: '80px', height: '80px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #E75480, #C2185B)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '3px solid white', color: 'white', fontSize: '1.5rem', fontWeight: 700
              }}>
                {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2)}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <h1 className="heading-1" style={{ marginBottom: '0.5rem' }}>
                Bonjour, {user.name}
              </h1>
              {user.enrolledPrograms.length === 0 && user.role !== 'admin' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <p className="body-medium" style={{ color: 'var(--text-muted)' }}>Aucune formation souscrite</p>
                  <a href="/programs" className="btn-primary" style={{ padding: '0.4rem 1rem', fontSize: '0.8rem' }}>Découvrir les formations</a>
                </div>
              ) : user.enrolledPrograms.length > 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="body-medium">Formation :</span>
                  <select
                    value={selectedProgramId}
                    onChange={(e) => {
                      setSelectedProgramId(e.target.value);
                      setSelectedCourse(null);
                      setActiveTab('overview');
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--border-light)',
                      background: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {user.enrolledPrograms.map(progId => {
                      const prog = VAE_PROGRAMS.find(p => p.id === progId);
                      return (
                        <option key={progId} value={progId}>
                          {prog?.title} - {prog?.fullName}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : (
                <p className="body-medium">
                  Formation : {enrolledProgram?.title} - {enrolledProgram?.fullName}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div style={{ 
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-light)',
          paddingBottom: '1rem'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Vue d'ensemble
          </button>
          {(!isKit || kitHasElearning) && (
          <button
            onClick={() => setActiveTab('courses')}
            className={activeTab === 'courses' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Mes Cours
          </button>
          )}
          <button
            onClick={() => setActiveTab('bookings')}
            className={activeTab === 'bookings' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Mes Séances
          </button>
          {currentFormula && !isKit && (
          <>
          <button
            onClick={() => setActiveTab('oral-prep')}
            className={activeTab === 'oral-prep' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Préparation à l'oral
          </button>
          </>
          )}
          <button
            onClick={() => setActiveTab('stress-management')}
            className={activeTab === 'stress-management' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Gestion du stress
          </button>
          <button
            data-testid="tab-purchases"
            onClick={() => setActiveTab('purchases')}
            className={activeTab === 'purchases' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Mes Achats
          </button>
          {userPurchases.some(p => p.programId === 'veille-pro' && p.status === 'completed') && (
          <button
            data-testid="tab-veille-pro"
            onClick={() => setActiveTab('veille-pro')}
            className={activeTab === 'veille-pro' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Veille Pro
          </button>
          )}
          {currentFormula && currentFormula !== 'bronze' && !isKit && (
          <button
            data-testid="tab-mon-parcours"
            onClick={() => { setActiveTab('mon-parcours'); fetchUserProgress(user.id).then(setUserProgressList).catch(() => {}); }}
            className={activeTab === 'mon-parcours' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Mon Parcours
          </button>
          )}
          {['deap', 'deeje', 'cap-aepe', 'tp'].includes(selectedProgramId) && (currentFormula || user.role === 'admin') && (
          <button
            data-testid="tab-ressources-gratuites"
            onClick={() => setActiveTab('ressources-gratuites')}
            className={activeTab === 'ressources-gratuites' ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
          >
            Ressources Gratuites
          </button>
          )}
        </div>


        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <OverviewTab
            currentFormula={currentFormula}
            isKit={isKit}
            kitBlocNum={kitBlocNum}
            entitlements={entitlements}
            programEntitlements={programEntitlements}
            subscriptionInfo={subscriptionInfo}
            user={user}
            confirmedBookings={confirmedBookings}
            selectedProgramId={selectedProgramId}
            enrolledProgram={enrolledProgram}
            userCourses={userCourses}
            allCourses={allCourses}
            handleCourseClick={handleCourseClick}
            setActiveTab={setActiveTab}
            kitHasElearning={kitHasElearning}
            kitDownloadPath={kitDownloadPath}
            currentKitData={currentKitData}
            decouverteBooking={decouverteBooking}
          />
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <CoursesTab
            subscriptionInfo={subscriptionInfo}
            user={user}
            selectedProgramId={selectedProgramId}
            enrolledProgram={enrolledProgram}
            userCourses={userCourses}
            getCourseProgress={getCourseProgress}
            handleCourseClick={handleCourseClick}
          />
        )}

        {/* Course Detail */}
        {activeTab === 'course-detail' && selectedCourse && (
          <CourseDetailTab
            selectedCourse={selectedCourse}
            quizDataCache={quizDataCache}
            quizAnswers={quizAnswers}
            quizSubmitted={quizSubmitted}
            onBack={() => { setActiveTab('courses'); setSelectedCourse(null); setQuizSubmitted(false); setQuizAnswers({}); }}
            onQuizAnswer={handleQuizAnswer}
            onQuizSubmit={handleQuizSubmit}
            onCompleteCourse={handleCompleteCourse}
            onQuizRetry={handleQuizRetry}
          />
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <BookingsTab
            currentFormula={currentFormula}
            isKit={isKit}
            kitBlocNum={kitBlocNum}
            currentKitData={currentKitData}
            entitlements={entitlements}
            userBookings={userBookings}
            user={user}
            selectedProgramId={selectedProgramId}
            rescheduleBookingId={rescheduleBookingId}
            setRescheduleBookingId={setRescheduleBookingId}
            rescheduleDate={rescheduleDate}
            setRescheduleDate={setRescheduleDate}
            rescheduleSlot={rescheduleSlot}
            setRescheduleSlot={setRescheduleSlot}
            availableSlots={availableSlots}
            setAvailableSlots={setAvailableSlots}
            setUserBookings={setUserBookings}
            activeBookingsCount={activeBookingsCount}
          />
        )}

        {/* Oral Preparation Tab */}
        {activeTab === 'oral-prep' && (
          <OralPrepTab
            currentFormula={currentFormula}
            entitlements={entitlements}
            selectedProgramId={selectedProgramId}
            userBookings={userBookings}
            rescheduleBookingId={rescheduleBookingId}
            setRescheduleBookingId={setRescheduleBookingId}
            rescheduleDate={rescheduleDate}
            setRescheduleDate={setRescheduleDate}
            rescheduleSlot={rescheduleSlot}
            setRescheduleSlot={setRescheduleSlot}
            availableSlots={availableSlots}
            setAvailableSlots={setAvailableSlots}
            setUserBookings={setUserBookings}
            user={user}
            hasPresentationPersoAccess={hasPresentationPersoAccess}
          />
        )}

        {/* Stress Management Tab */}
        {activeTab === 'stress-management' && (
          <StressManagementTab hasAccess={hasStressAccess} user={user} selectedProgramId={selectedProgramId} />
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <PurchasesTab userPurchases={userPurchases} />
        )}

        {/* Veille Pro Tab */}
        {activeTab === 'veille-pro' && (
          <VeilleProTab userPurchases={userPurchases} user={user} />
        )}

        {/* Mon Parcours Tab */}
        {activeTab === 'mon-parcours' && (
          <MonParcoursTab
            enrolledProgram={enrolledProgram}
            selectedProgramId={selectedProgramId}
            allCourses={allCourses}
            userProgressList={userProgressList}
            recevabiliteList={recevabiliteList}
            livret2Corrections={livret2Corrections}
            userProgressData={userProgressData}
            accompanimentData={accompanimentData}
            currentFormula={currentFormula}
            entitlements={entitlements}
            user={user}
            dragOver={dragOver}
            setDragOver={setDragOver}
            uploading={uploading}
            setUploading={setUploading}
            setRecevabiliteList={setRecevabiliteList}
          />
        )}

        {/* Ressources Gratuites Tab */}
        {activeTab === 'ressources-gratuites' && (
          <RessourcesGratuitesTab />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
